import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  OnDestroy,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { HolidayRegionCode, TimelineTimeframe } from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  ShiftTimelineDataState,
  ShiftTimelineFilterStateService,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  TimelineSetSearchTerm,
} from '@wilson/shift-timeline/state';
import { Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'wilson-shift-timeline-gantt-header',
  templateUrl: './shift-timeline-gantt-header.component.html',
  styleUrls: ['./shift-timeline-gantt-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShiftTimelineGanttHeaderComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );

  @Input()
  set scrollLeftPx(value: number) {
    this.headerContainer.nativeElement.scrollLeft = value;
  }

  @Input() stickyHasShadow?: boolean;

  @Input()
  viewport!: HTMLElement;

  @Output()
  visibleRangeChanged = new EventEmitter<{
    start: Date | null;
    end: Date | null;
  }>();

  @ViewChild('headerContainer', { static: true })
  headerContainer!: ElementRef;

  protected zoomLevel$ = this.store.select(
    ShiftTimelineSettingsState.zoomLevel,
  );

  protected hoursRange$!: Observable<string[]>;
  protected resourcesColumnWidthPx$!: Observable<number>;
  protected frameLength$!: Observable<number>;
  protected publicHolidaysRegion$!: Observable<HolidayRegionCode | undefined>;
  protected isHomeLocationKpiEnabled$!: Observable<boolean>;
  protected visibleDaysRange$!: Observable<TimelineTimeframe>;
  protected timeframe$!: Observable<Interval>;

  ngOnInit(): void {
    this.timeframe$ = this.store.select(ShiftTimelineDataState.timeframe);
    this.visibleDaysRange$ = this.store.select(
      ShiftTimelineDataState.visibleDateRange,
    );
    this.publicHolidaysRegion$ = this.store.select(
      AuthState.publicHolidaysRegion,
    );
    this.hoursRange$ = this.store.select(ShiftTimelineSettingsState.hoursRange);

    this.resourcesColumnWidthPx$ = this.store.select(
      ShiftTimelineSettingsState.resourcesColumnWidthPx,
    );

    this.frameLength$ = this.store.select(ShiftTimelineState.frameLength);

    this.isHomeLocationKpiEnabled$ = this.store.select(AuthState.userId).pipe(
      switchMap((userId) => {
        if (userId) {
          return this.store.select(
            ShiftTimelinePreferredSettingsState.showHomeLocation(userId),
          );
        } else {
          return of(false);
        }
      }),
    );
  }

  protected updateSearchTerm(searchTerm: string): void {
    this.store.dispatch(new TimelineSetSearchTerm(searchTerm));
    this.shiftTimelineFilterStateService.searchTerm = searchTerm;
  }

  ngOnDestroy(): void {
    // Flush to prevent memory leak
    this.isHomeLocationKpiEnabled$ = null as any;
  }
}
